/** Class names shared by the tabbed sidebar's controls, on its tint. */

/** Hover fill for a control on the tint. */
export const SIDEBAR_HOVER = 'hover:bg-black/[0.05] dark:hover:bg-white/[0.06]';

/** Icon colour on the tint. */
export const SIDEBAR_ICON = 'text-gray-600 dark:text-gray-400';

/** The translucent white of the command bar, where the tiles rest too. */
export const SIDEBAR_FILL = 'bg-white/60 dark:bg-white/[0.06]';

/**
 * A favorite tile that is not selected, as Arc draws its tiles: the
 * translucent fill at rest, lit to a near solid square with a hairline edge
 * on hover or keyboard focus, and dipped back toward the tint while pressed.
 * In light mode the lit square stops a step short of solid white, which is
 * kept, with its drop shadow, for the selected tile's raised card.
 *
 * Dark mode names its fills in hex, off the white/[0.0x] steps: index.css
 * paints those #1a1a1a !important in dark mode, whatever their variant, which
 * would pin the tile at rest when hovered, focused or pressed. The rest fill
 * is that same #1a1a1a, as the command bar shows it.
 */
export const TILE_FILL = [
  'bg-white/60 dark:bg-[#1a1a1a]',
  'hover:bg-white/85 hover:shadow-[0_0_0_0.5px_rgba(17,17,17,0.06)]',
  'focus-visible:bg-white/85 focus-visible:shadow-[0_0_0_0.5px_rgba(17,17,17,0.06)]',
  'active:bg-white/75',
  'dark:hover:bg-[#262626] dark:hover:shadow-[0_0_0_0.5px_rgba(255,255,255,0.06)]',
  'dark:focus-visible:bg-[#262626] dark:focus-visible:shadow-[0_0_0_0.5px_rgba(255,255,255,0.06)]',
  'dark:active:bg-[#202020]',
].join(' ');

/** How a tile eases between rest, hover and pressed. */
export const TILE_TRANSITION =
  'transition-[color,background-color,box-shadow] duration-150 ease-out';

/**
 * The selected item: a white card raised off the tint, as the tab for the
 * chat on screen. In dark mode it takes the page card's colour.
 */
export const RAISED_CARD =
  'bg-white shadow-[0_1px_3px_rgba(17,17,17,0.08),0_0_0_0.5px_rgba(17,17,17,0.08)] dark:bg-[#1E1E1E] dark:shadow-[0_0_0_0.5px_rgba(255,255,255,0.1)]';

/**
 * An item's label: bold while it is selected, on its white card. Bold
 * tightens its tracking to the regular width, so "Find People" still fits
 * its tile and nothing shifts.
 */
export const itemLabel = (selected: boolean) =>
  selected ? 'font-semibold tracking-[-0.02em]' : '';

/** The thin rule between the sidebar's groups. */
export const SIDEBAR_RULE = 'bg-black/[0.12] dark:bg-white/[0.12]';

/**
 * One cell of the page rail (the folded sidebar): every control on it
 * is 40 wide by 32 high, rounded 8px, its 16px icon centred, so the column
 * lines up and hover and the selected card share one shape.
 */
export const RAIL_CELL = 'h-8 w-10 rounded-[8px]';

/** A column of rail cells, 2px apart. */
export const RAIL_GRID = 'grid auto-rows-[32px] grid-cols-[40px] gap-0.5';

/**
 * A rail cell for a control another component draws (the account menu, the
 * bell, What's new): the slot sizes the control to `RAIL_CELL`, and folds
 * away when the control renders nothing.
 */
export const RAIL_SLOT =
  'grid place-items-center empty:hidden *:h-8 *:w-10 *:rounded-[8px]';

/** The rule between the rail's groups, 6px clear of each. */
export const RAIL_RULE = 'my-1.5 w-6';

/**
 * An item being dragged: the raised card lifted further off the tint, and
 * tipped a little, as Arc draws it.
 */
export const LIFTED_CARD =
  'bg-white shadow-[0_12px_24px_rgba(17,17,17,0.16),0_2px_6px_rgba(17,17,17,0.08)] -rotate-2 dark:bg-[#1E1E1E] dark:shadow-[0_12px_24px_rgba(0,0,0,0.5),0_0_0_0.5px_rgba(255,255,255,0.1)]';
